import { useMemo, useState } from 'react';
import { DefaultViewHint } from '@/features/ai-agents/chat/default-view-hint';
import { CHART_VIEWS } from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import {
  ParsedTable,
  TableSort,
  TableViewType,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  findColumnIndexByLabel,
  findDefaultXAxisColumnIndex,
  getCurrentColumnOrder,
  getNextSort,
  sortTableRows,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface UseVisualizationStateOptions {
  chartNumericColumnIndexes: number[];
  defaultViewHint: DefaultViewHint | null;
  defaultXColumnIndex: number;
  isVariantReport: boolean;
  table: ParsedTable;
  variantColumnIndex: number;
}

export function useVisualizationState({
  chartNumericColumnIndexes,
  defaultViewHint,
  defaultXColumnIndex,
  isVariantReport,
  table,
  variantColumnIndex,
}: UseVisualizationStateOptions) {
  const hintedView = resolveHintedView(
    defaultViewHint,
    chartNumericColumnIndexes,
  );
  const hintedXColumnIndex = resolveHintedColumnIndex(
    table.headers,
    defaultViewHint?.xAxis ?? null,
    (columnIndex) => columnIndex !== variantColumnIndex,
  );
  const hintedYColumnIndex = resolveHintedColumnIndex(
    table.headers,
    defaultViewHint?.yAxis ?? null,
    (columnIndex) => chartNumericColumnIndexes.includes(columnIndex),
  );
  const [view, setView] = useState<TableViewType>(
    () => hintedView ?? (isVariantReport ? `line` : `table`),
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    table.headers.map((_header, index) => index),
  );
  const [sort, setSort] = useState<TableSort | null>(null);
  const [xColumnIndex, setXColumnIndex] = useState(
    hintedXColumnIndex ?? defaultXColumnIndex,
  );
  const [yColumnIndex, setYColumnIndex] = useState(
    hintedYColumnIndex ??
      chartNumericColumnIndexes.find(
        (index) => index !== defaultXColumnIndex,
      ) ??
      chartNumericColumnIndexes[0] ??
      0,
  );
  const orderedColumnIndexes = getCurrentColumnOrder(
    columnOrder,
    table.headers.length,
  );
  const availableXColumnIndexes = orderedColumnIndexes.filter(
    (columnIndex) => columnIndex !== variantColumnIndex,
  );
  const selectedXColumnIndex = availableXColumnIndexes.includes(xColumnIndex)
    ? xColumnIndex
    : findDefaultXAxisColumnIndex(
        table.headers.length,
        chartNumericColumnIndexes,
        variantColumnIndex,
      );
  const selectedYColumnIndex = chartNumericColumnIndexes.includes(yColumnIndex)
    ? yColumnIndex
    : chartNumericColumnIndexes.find(
        (index) => index !== selectedXColumnIndex,
      ) ??
      chartNumericColumnIndexes[0] ??
      0;
  const currentSort = table.headers[sort?.columnIndex] ? sort : null;
  const sortedRows = useMemo(
    () =>
      sortTableRows(
        table.rows,
        currentSort?.columnIndex,
        currentSort?.direction,
      ),
    [table.rows, currentSort?.columnIndex, currentSort?.direction],
  );
  const canShowChart = chartNumericColumnIndexes.length > 0;
  const activeView = isVariantReport && view !== `table` ? `line` : view;
  const availableChartViews = isVariantReport
    ? CHART_VIEWS.filter((chartView) => chartView.value === `line`)
    : CHART_VIEWS;

  const handleSort = (columnIndex: number) => {
    setSort((currentSort) => getNextSort(currentSort, columnIndex));
  };

  return {
    activeView,
    availableChartViews,
    availableXColumnIndexes,
    canShowChart,
    currentSort,
    handleSort,
    orderedColumnIndexes,
    selectedXColumnIndex,
    selectedYColumnIndex,
    setColumnOrder,
    setView,
    setXColumnIndex,
    setYColumnIndex,
    sortedRows,
  };
}

function resolveHintedView(
  defaultViewHint: DefaultViewHint | null,
  chartNumericColumnIndexes: number[],
): TableViewType | null {
  if (!defaultViewHint?.type) {
    return null;
  }

  const canShowChart = chartNumericColumnIndexes.length > 0;

  return defaultViewHint.type === `table` || canShowChart
    ? defaultViewHint.type
    : null;
}

function resolveHintedColumnIndex(
  headers: string[],
  hintedLabel: string | null,
  isValidColumnIndex: (columnIndex: number) => boolean,
): number | null {
  const columnIndex = findColumnIndexByLabel(headers, hintedLabel);

  return columnIndex >= 0 && isValidColumnIndex(columnIndex)
    ? columnIndex
    : null;
}
